// Hook React enveloppant Web Speech API pour la dictée vocale (P3 chantier DAL).
// Cible : Chrome Android, Safari iOS 14.5+, Edge desktop. Firefox ne supporte pas
// SpeechRecognition (le bouton micro reste caché via `supported=false`).
//
// Usage :
//   const { supported, listening, transcript, start, stop, reset } = useSpeechRecognition({ lang: "fr-FR" });
//
// Le hook accumule les `final results` dans `transcript`. L'appelant lit la valeur
// au stop pour merger avec son state local (cf. mergeTranscript dans speechHelpers).

// ⚠️ PREPROD SEULEMENT (N°593) — capture des événements bruts ; inerte sans ?capture_dictee=1.
import { instrumenterCapture } from "./captureDictee";
import { useCallback, useEffect, useRef, useState } from "react";
import { isSpeechSupported } from "../components/dal/speechHelpers";

export default function useSpeechRecognition({ lang = "fr-FR", continuous = true } = {}) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  // 🔑 REF, ⛔ pas une variable d'effet : `start()` vit HORS de l'effet et doit
  // pouvoir VIDER les résultats. Sans ça, une seconde dictée repartirait avec
  // les segments de la première — la duplication, par la porte de derrière.
  const finalsRef = useRef([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isSpeechSupported(window)) {
      setSupported(false);
      return;
    }
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Ctor();
    rec.continuous = continuous;
    rec.interimResults = true;
    rec.lang = lang;

    // 🔴 LES RÉSULTATS SONT INDEXÉS, ⛔ PAS EMPILÉS.
    //
    // ⚠️ CE QUE FAISAIT L'ANCIEN CODE : `setTranscript(prev => prev + finalText)`
    // — un APPEND aveugle. Or la Web Speech API fait DEUX choses parfaitement
    // normales que l'append ⛔ ne supporte pas :
    //   · elle RÉ-ÉMET des résultats déjà finaux (`resultIndex` revient en
    //     arrière en mode continu) ⇒ le texte était AJOUTÉ UNE SECONDE FOIS
    //     ⇒ « mots répétés » ;
    //   · elle RÉVISE un résultat final après coup (« sintofer » corrigé)
    //     ⇒ l'ancien ET le nouveau se retrouvaient à la suite.
    // Et en aval, `extractTranscriptDelta` découpait par LONGUEUR : sur un
    // transcript réécrit, la coupe tombait AU MILIEU D'UN MOT ⇒ « exterieure-
    // protection », « polyanebrossage ». Les deux symptômes de JJ, une cause.
    // 🔑 ⇒ On INDEXE : `finals[i]` est ÉCRASÉ, jamais empilé. Ré-émettre ou
    // réviser le résultat i redonne exactement le même texte — l'opération est
    // IDEMPOTENTE, donc ⛔ ni duplication ⛔ ni mot coupé, par construction.
    const finals = finalsRef.current;
    rec.onresult = (event) => {
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finals[i] = res[0].transcript.trim();
        else interimText += res[0].transcript;
      }
      setTranscript(finals.filter(Boolean).join(" "));
      setInterimTranscript(interimText);
    };
    rec.onerror = (e) => {
      setError(e.error || "speech-recognition-error");
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      setInterimTranscript("");
    };

    instrumenterCapture(rec);   // PREPROD N°593 — ⛔ ne modifie aucun comportement
    recognitionRef.current = rec;
    setSupported(true);
    return () => {
      try { rec.stop(); } catch {}
      recognitionRef.current = null;
    };
  }, [lang, continuous]);

  const start = useCallback(() => {
    if (!recognitionRef.current || listening) return;
    setError(null);
    finalsRef.current.length = 0;
    setTranscript("");
    setInterimTranscript("");
    try {
      recognitionRef.current.start();
      setListening(true);
    } catch (e) {
      // start() peut throw si déjà actif
      setError(e.message || String(e));
    }
  }, [listening]);

  const stop = useCallback(() => {
    if (!recognitionRef.current || !listening) return;
    try { recognitionRef.current.stop(); } catch {}
  }, [listening]);

  const reset = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
    setError(null);
  }, []);

  return { supported, listening, transcript, interimTranscript, error, start, stop, reset };
}
